import { useCallback, useEffect, useMemo, useState } from 'react';
import { Plus, Search } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { Link, useSearchParams } from 'react-router-dom';
import { PageLoadingIndicator } from '@kioskgaming/page-loading';
import AppLayout from '../components/layout/AppLayout';
import {
  fetchManagedAgents,
  fetchManagedPlayers,
  fetchManagedShops,
  moveManagedPlayer,
  moveManagedShop,
  suspendManagedAgent,
  unsuspendManagedAgent,
} from '../services/api';
import type {
  SuperManagedAgent as ManagedAgent,
  SuperManagedPlayer as ManagedPlayer,
  SuperManagedShop as ManagedShop
} from '../types';
import { ACTION, accountStatus, PAGE } from '@kioskgaming/ui';

const toPercent = (rate: number | string | undefined) => {
  const n = Number(rate ?? 0);
  if (!Number.isFinite(n)) return '0.00';
  return (n * 100).toFixed(2);
};

type ManagementSection = 'agents' | 'shops' | 'players';
type SortDir = 'asc' | 'desc';

function SortTh({
  label,
  active,
  dir,
  onClick,
  className,
}: {
  label: string;
  active: boolean;
  dir: SortDir;
  onClick: () => void;
  className?: string;
}) {
  return (
    <th className={className ?? 'px-4 py-3'}>
      <button
        type="button"
        onClick={onClick}
        className="inline-flex items-center gap-1 font-medium text-gray-700 hover:text-gray-900"
        aria-label={`Sort by ${label}`}
      >
        {label}
        <span className="text-[10px] text-gray-400" aria-hidden>
          {active ? (dir === 'asc' ? '↑' : '↓') : '↕'}
        </span>
      </button>
    </th>
  );
}

export default function SuperAgentManagementPage({
  section = 'agents',
}: {
  section?: ManagementSection;
}) {
  const [loading, setLoading] = useState(true);
  const [agents, setAgents] = useState<ManagedAgent[]>([]);
  const [shops, setShops] = useState<ManagedShop[]>([]);
  const [players, setPlayers] = useState<ManagedPlayer[]>([]);
  const [agentSearch, setAgentSearch] = useState('');
  const [shopSearch, setShopSearch] = useState('');
  const [shopAgentFilter, setShopAgentFilter] = useState('');
  const [playerSearch, setPlayerSearch] = useState('');
  const [playerShopFilter, setPlayerShopFilter] = useState('');
  const [moveShopTarget, setMoveShopTarget] = useState<ManagedShop | null>(null);
  const [targetAgentId, setTargetAgentId] = useState('');
  const [movingShop, setMovingShop] = useState(false);
  const [movePlayerTarget, setMovePlayerTarget] = useState<ManagedPlayer | null>(null);
  const [targetShopId, setTargetShopId] = useState('');
  const [movingPlayer, setMovingPlayer] = useState(false);
  const [agentCostSort, setAgentCostSort] = useState<SortDir | null>('desc');
  const [shopCostSort, setShopCostSort] = useState<SortDir | null>('desc');
  const [playerNameSort, setPlayerNameSort] = useState<SortDir | null>('asc');
  const [searchParams] = useSearchParams();
  const highlightPlayerId = section === 'players' ? searchParams.get('playerId')?.trim() || '' : '';

  const toggleAgentCostSort = () => {
    setAgentCostSort((prev) => (prev === 'desc' ? 'asc' : 'desc'));
  };
  const toggleShopCostSort = () => {
    setShopCostSort((prev) => (prev === 'desc' ? 'asc' : 'desc'));
  };
  const togglePlayerNameSort = () => {
    setPlayerNameSort((prev) => (prev === 'desc' ? 'asc' : 'desc'));
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [agentsRes, shopsRes, playersRes] = await Promise.all([
        fetchManagedAgents(),
        fetchManagedShops(),
        fetchManagedPlayers()
      ]);
      setAgents(agentsRes.data ?? []);
      setShops(shopsRes.data ?? []);
      setPlayers(playersRes.data ?? []);
    } catch {
      toast.error('Failed to load Super Agent data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const filteredAgents = useMemo(() => {
    const q = agentSearch.trim().toLowerCase();
    const list = !q
      ? agents
      : agents.filter((a) => `${a.name} ${a.email} ${a.phone ?? ''}`.toLowerCase().includes(q));
    if (!agentCostSort) return list;
    const dir = agentCostSort === 'asc' ? 1 : -1;
    return [...list].sort(
      (a, b) => (Number(a.costRate ?? 0) - Number(b.costRate ?? 0)) * dir,
    );
  }, [agents, agentSearch, agentCostSort]);

  const filteredShops = useMemo(() => {
    const q = shopSearch.trim().toLowerCase();
    const list = shops.filter((s) => {
      const agentMatched = shopAgentFilter ? s.agent?.id === shopAgentFilter : true;
      if (!agentMatched) return false;
      if (!q) return true;
      return `${s.name} ${s.username ?? ''} ${s.email} ${s.phone ?? ''} ${s.agent?.name ?? ''}`
        .toLowerCase()
        .includes(q);
    });
    if (!shopCostSort) return list;
    const dir = shopCostSort === 'asc' ? 1 : -1;
    return [...list].sort(
      (a, b) => (Number(a.costRate ?? 0) - Number(b.costRate ?? 0)) * dir,
    );
  }, [shops, shopSearch, shopAgentFilter, shopCostSort]);

  const filteredPlayers = useMemo(() => {
    let list: ManagedPlayer[];
    if (highlightPlayerId) {
      const match = players.filter((p) => p.id === highlightPlayerId);
      list = match.length > 0 ? match : players;
    } else {
      const q = playerSearch.trim().toLowerCase();
      list = players.filter((p) => {
        const shopMatched = playerShopFilter ? p.shop_id === playerShopFilter : true;
        if (!shopMatched) return false;
        if (!q) return true;
        return `${p.username ?? ''} ${p.email ?? ''} ${p.phone ?? ''} ${p.shop_name ?? ''} ${p.agent_name ?? ''}`
          .toLowerCase()
          .includes(q);
      });
    }
    if (!playerNameSort) return list;
    const dir = playerNameSort === 'asc' ? 1 : -1;
    return [...list].sort((a, b) => {
      const an = String(a.username || a.email || a.id).toLowerCase();
      const bn = String(b.username || b.email || b.id).toLowerCase();
      return an.localeCompare(bn) * dir;
    });
  }, [highlightPlayerId, playerSearch, playerShopFilter, players, playerNameSort]);

  const onSuspendToggle = async (item: ManagedAgent) => {
    try {
      const res =
        item.status === 'suspended' ? await unsuspendManagedAgent(item.id) : await suspendManagedAgent(item.id);
      if (!res.success) {
        toast.error(res.message || 'Action failed');
        return;
      }
      toast.success(item.status === 'suspended' ? 'Agent reactivated' : 'Agent suspended');
      await load();
    } catch {
      toast.error('Action failed');
    }
  };

  const onOpenMoveShop = (shop: ManagedShop) => {
    setMoveShopTarget(shop);
    setTargetAgentId('');
  };

  const onConfirmMoveShop = async () => {
    if (!moveShopTarget || !targetAgentId) return;
    setMovingShop(true);
    try {
      const res = await moveManagedShop(moveShopTarget.id, targetAgentId);
      if (!res.success) {
        toast.error(res.message || 'Could not move Shop');
        return;
      }
      toast.success('Shop moved successfully');
      setMoveShopTarget(null);
      setTargetAgentId('');
      await load();
    } catch {
      toast.error('Could not move Shop');
    } finally {
      setMovingShop(false);
    }
  };

  const onOpenMovePlayer = (player: ManagedPlayer) => {
    setMovePlayerTarget(player);
    setTargetShopId('');
  };

  const onConfirmMovePlayer = async () => {
    if (!movePlayerTarget || !targetShopId) return;
    setMovingPlayer(true);
    try {
      const res = await moveManagedPlayer(movePlayerTarget.id, targetShopId);
      if (!res.success) {
        toast.error(res.message || 'Could not move Player');
        return;
      }
      toast.success('Player moved successfully');
      setMovePlayerTarget(null);
      setTargetShopId('');
      await load();
    } catch {
      toast.error('Could not move Player');
    } finally {
      setMovingPlayer(false);
    }
  };

  const pageTitle = section === 'agents' ? PAGE.agents : section === 'shops' ? PAGE.shops : PAGE.players;
  const pageDescription =
    section === 'agents'
      ? 'Manage all Agents in one place'
      : section === 'shops'
        ? 'Manage all Shops in one place'
        : 'Manage all Players in one place';

  return (
    <AppLayout contentFullWidth>
      <div className="space-y-8">
        <section className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{pageTitle}</h1>
            <p className="text-sm text-gray-600">{pageDescription}</p>
          </div>
          {section === 'agents' && (
            <Link
              to="/agents/new"
              className="inline-flex items-center gap-2 rounded-lg bg-orange-600 px-4 py-2 text-sm font-semibold text-white hover:bg-orange-700"
            >
              <Plus className="h-4 w-4" />
              Create Agent
            </Link>
          )}
        </section>

        {section === 'agents' && (
          <section className="rounded-xl border border-orange-100 bg-white shadow-sm">
          <div className="border-b border-gray-100 p-4">
            <h2 className="text-lg font-semibold">Agents</h2>
            <div className="relative mt-3 max-w-md">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
              <input className="w-full rounded-lg border border-gray-200 py-2 pl-10 pr-3 text-sm" placeholder="Search Agent" value={agentSearch} onChange={(e) => setAgentSearch(e.target.value)} />
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full text-left text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3">Name</th>
                  <th className="px-4 py-3">Email</th>
                  <th className="px-4 py-3">Phone</th>
                  <SortTh
                    label="Cost (%)"
                    active={agentCostSort != null}
                    dir={agentCostSort ?? 'desc'}
                    onClick={toggleAgentCostSort}
                  />
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3 text-right">Manage</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td className="px-4 py-6" colSpan={6}>
                      <PageLoadingIndicator variant="inline" label="Loading…" />
                    </td>
                  </tr>
                ) : filteredAgents.length === 0 ? (
                  <tr><td className="px-4 py-4 text-gray-500" colSpan={6}>No Agents found</td></tr>
                ) : filteredAgents.map((a) => (
                  <tr key={a.id} className="border-t border-gray-100">
                    <td className="px-4 py-3 font-medium">
                      <Link
                        to={`/agents/${a.id}`}
                        className="text-blue-700 underline-offset-2 hover:text-blue-800 hover:underline"
                      >
                        {a.name}
                      </Link>
                    </td>
                    <td className="px-4 py-3">{a.email}</td>
                    <td className="px-4 py-3">{a.phone || '-'}</td>
                    <td className="px-4 py-3">{toPercent(a.costRate)}</td>
                    <td className="px-4 py-3">{accountStatus(a.status).label}</td>
                    <td className="px-4 py-3 text-right">
                      <div className="inline-flex gap-2">
                        {a.status !== 'terminated' && (
                          <button type="button" className="rounded border border-amber-200 bg-amber-50 px-2 py-1 text-xs" onClick={() => void onSuspendToggle(a)}>
                            {a.status === 'suspended' ? ACTION.reactivate : 'Suspend'}
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          </section>
        )}

        {section === 'shops' && (
          <section className="rounded-xl border border-orange-100 bg-white shadow-sm">
          <div className="border-b border-gray-100 p-4">
            <h2 className="text-lg font-semibold">Shops</h2>
            <div className="mt-3 grid gap-3 md:grid-cols-2">
              <div className="relative">
                <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                <input className="w-full rounded-lg border border-gray-200 py-2 pl-10 pr-3 text-sm" placeholder="Search Shop" value={shopSearch} onChange={(e) => setShopSearch(e.target.value)} />
              </div>
              <select
                className="rounded-lg border border-gray-200 px-3 py-2 text-sm"
                value={shopAgentFilter}
                onChange={(e) => setShopAgentFilter(e.target.value)}
              >
                <option value="">All Agents</option>
                {agents.map((agent) => (
                  <option key={agent.id} value={agent.id}>
                    {agent.name}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full text-left text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3">Shop</th>
                  <th className="px-4 py-3">Email</th>
                  <th className="px-4 py-3">Agent</th>
                  <SortTh
                    label="Cost (%)"
                    active={shopCostSort != null}
                    dir={shopCostSort ?? 'desc'}
                    onClick={toggleShopCostSort}
                  />
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3 text-right">Manage</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td className="px-4 py-6" colSpan={6}>
                      <PageLoadingIndicator variant="inline" label="Loading…" />
                    </td>
                  </tr>
                ) : filteredShops.length === 0 ? (
                  <tr><td className="px-4 py-4 text-gray-500" colSpan={6}>No Shops found</td></tr>
                ) : filteredShops.map((s) => (
                  <tr key={s.id} className="border-t border-gray-100">
                    <td className="px-4 py-3 font-medium">
                      <Link
                        to={`/shops/${s.id}`}
                        className="text-blue-700 underline-offset-2 hover:text-blue-800 hover:underline"
                      >
                        {s.name}
                      </Link>
                    </td>
                    <td className="px-4 py-3">{s.email}</td>
                    <td className="px-4 py-3">{s.agent?.name || '-'}</td>
                    <td className="px-4 py-3">{toPercent(s.costRate)}</td>
                    <td className="px-4 py-3">{accountStatus(s.status).label}</td>
                    <td className="px-4 py-3 text-right">
                      <button
                        type="button"
                        className="rounded border border-blue-200 bg-blue-50 px-2 py-1 text-xs text-blue-700"
                        onClick={() => onOpenMoveShop(s)}
                      >
                        Move
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          </section>
        )}

        {section === 'players' && (
          <section className="rounded-xl border border-orange-100 bg-white shadow-sm">
          <div className="border-b border-gray-100 p-4">
            <h2 className="text-lg font-semibold">Players</h2>
            <div className="mt-3 grid gap-3 md:grid-cols-2">
              <div className="relative">
                <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                <input
                  className="w-full rounded-lg border border-gray-200 py-2 pl-10 pr-3 text-sm"
                  placeholder="Search Player"
                  value={playerSearch}
                  onChange={(e) => setPlayerSearch(e.target.value)}
                />
              </div>
              <select
                className="rounded-lg border border-gray-200 px-3 py-2 text-sm"
                value={playerShopFilter}
                onChange={(e) => setPlayerShopFilter(e.target.value)}
              >
                <option value="">All Shops</option>
                {shops.map((shop) => (
                  <option key={shop.id} value={shop.id}>
                    {shop.name}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full text-left text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <SortTh
                    label="Username"
                    active={playerNameSort != null}
                    dir={playerNameSort ?? 'asc'}
                    onClick={togglePlayerNameSort}
                  />
                  <th className="px-4 py-3">Email</th>
                  <th className="px-4 py-3">Phone</th>
                  <th className="px-4 py-3">Shop</th>
                  <th className="px-4 py-3">Agent</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3 text-right">Manage</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td className="px-4 py-6" colSpan={7}>
                      <PageLoadingIndicator variant="inline" label="Loading…" />
                    </td>
                  </tr>
                ) : filteredPlayers.length === 0 ? (
                  <tr><td className="px-4 py-4 text-gray-500" colSpan={7}>No Players found</td></tr>
                ) : filteredPlayers.map((p) => (
                  <tr
                    key={p.id}
                    className={`border-t border-gray-100 ${
                      highlightPlayerId && p.id === highlightPlayerId ? 'bg-orange-50/80' : ''
                    }`}
                  >
                    <td className="px-4 py-3 font-medium">{p.username || '-'}</td>
                    <td className="px-4 py-3">{p.email || '-'}</td>
                    <td className="px-4 py-3">{p.phone || '-'}</td>
                    <td className="px-4 py-3">{p.shop_name || '-'}</td>
                    <td className="px-4 py-3">{p.agent_name || '-'}</td>
                    <td className="px-4 py-3">{accountStatus(p.status).label}</td>
                    <td className="px-4 py-3 text-right">
                      <button
                        type="button"
                        className="rounded border border-blue-200 bg-blue-50 px-2 py-1 text-xs text-blue-700"
                        onClick={() => onOpenMovePlayer(p)}
                      >
                        Move
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          </section>
        )}
      </div>

      {moveShopTarget && (
        <div
          className="fixed inset-0 z-50 overflow-y-auto bg-black/50 p-4 sm:flex sm:items-center sm:justify-center"
          role="dialog"
          aria-modal="true"
          aria-label="Move Shop"
        >
          <div className="w-full max-w-md rounded-xl bg-white p-5 shadow-lg max-h-[calc(100vh-2rem)] overflow-y-auto">
            <h3 className="text-lg font-semibold">Move Shop: {moveShopTarget.name}</h3>
            <p className="mt-1 text-sm text-gray-600">Select target Agent</p>
            <select
              className="mt-4 w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
              value={targetAgentId}
              onChange={(e) => setTargetAgentId(e.target.value)}
              disabled={movingShop}
            >
              <option value="">Select target Agent</option>
              {agents.map((agent) => (
                <option key={agent.id} value={agent.id} disabled={agent.id === moveShopTarget.agent?.id}>
                  {agent.name} ({agent.email})
                </option>
              ))}
            </select>
            <div className="mt-4 flex justify-end gap-2">
              <button type="button" className="rounded-lg border border-gray-200 px-4 py-2 text-sm" disabled={movingShop} onClick={() => setMoveShopTarget(null)}>
                Cancel
              </button>
              <button
                type="button"
                className="rounded-lg bg-orange-600 px-4 py-2 text-sm font-semibold text-white disabled:opacity-60"
                disabled={movingShop || !targetAgentId}
                onClick={() => void onConfirmMoveShop()}
              >
                {movingShop ? 'Moving…' : 'Move'}
              </button>
            </div>
          </div>
        </div>
      )}

      {movePlayerTarget && (
        <div
          className="fixed inset-0 z-50 overflow-y-auto bg-black/50 p-4 sm:flex sm:items-center sm:justify-center"
          role="dialog"
          aria-modal="true"
          aria-label="Move Player"
        >
          <div className="w-full max-w-md rounded-xl bg-white p-5 shadow-lg max-h-[calc(100vh-2rem)] overflow-y-auto">
            <h3 className="text-lg font-semibold">Move Player: {movePlayerTarget.username || movePlayerTarget.email || movePlayerTarget.id}</h3>
            <p className="mt-1 text-sm text-gray-600">Select target Shop</p>
            <select
              className="mt-4 w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
              value={targetShopId}
              onChange={(e) => setTargetShopId(e.target.value)}
              disabled={movingPlayer}
            >
              <option value="">Select target Shop</option>
              {shops.map((shop) => (
                <option key={shop.id} value={shop.id} disabled={shop.id === movePlayerTarget.shop_id}>
                  {shop.name} ({shop.agent?.name || 'Unknown Agent'})
                </option>
              ))}
            </select>
            <div className="mt-4 flex justify-end gap-2">
              <button type="button" className="rounded-lg border border-gray-200 px-4 py-2 text-sm" disabled={movingPlayer} onClick={() => setMovePlayerTarget(null)}>
                Cancel
              </button>
              <button
                type="button"
                className="rounded-lg bg-orange-600 px-4 py-2 text-sm font-semibold text-white disabled:opacity-60"
                disabled={movingPlayer || !targetShopId}
                onClick={() => void onConfirmMovePlayer()}
              >
                {movingPlayer ? 'Moving…' : 'Move'}
              </button>
            </div>
          </div>
        </div>
      )}
    </AppLayout>
  );
}
